import React, { useMemo, useRef } from 'react';
import { Handle, Position, NodeProps, useStore } from 'reactflow';
import { Plus } from 'lucide-react';
import { getNodeDefinition as getNodeDef } from '@/utils/nodeDefinitions';
import { useWorkflowContext } from '@/contexts/WorkflowContext';
import { NodeIcon } from '@/components/ui/node-icon';
import { NodeContextMenu } from '@/components/workflow/NodeContextMenu';
import { workflowColors } from '@/lib/workflow-colors';

interface SwitchNodeData {
  label?: string;
  isExecuting?: boolean;
  executionStatus?: 'idle' | 'running' | 'success' | 'failed';
  disabled?: boolean;
  settings?: { disabled?: boolean };
  parameters?: {
    mode?: 'rules' | 'expression';
    rules?: {
      values?: Array<{
        outputName?: string;
        conditions?: any;
      }>;
    };
    numberOfOutputs?: number;
    options?: {
      fallbackOutput?: 'none' | 'extra' | '0';
    };
  };
}

export default function SwitchNode({ data, type, id }: NodeProps<SwitchNodeData>) {
  const nodeType = type || 'switch';
  const { onAddNodeClick } = useWorkflowContext();

  const edges = useStore((state) => state.edges);
  const nodeRef = useRef<HTMLDivElement>(null);

  const displayDef = getNodeDef(nodeType);

  const isDisabled = data.disabled || data.settings?.disabled || false;
  const isExecuting = data.isExecuting || false;
  const executionStatus = data.executionStatus || 'idle';

  const nodeData = data?.parameters || {};
  const mode = nodeData.mode || 'rules';
  const fallbackOutput = nodeData.options?.fallbackOutput || 'none';

  // Calculate dynamic outputs based on configuration
  const outputs = useMemo(() => {
    const dynamicOutputs: Array<{ id: string; label: string; isFallback?: boolean }> = [];

    if (mode === 'rules') {
      const rules = nodeData.rules?.values || [];
      rules.forEach((rule, index) => {
        dynamicOutputs.push({
          id: `output_${index}`,
          label: rule.outputName || `Output ${index}`,
        });
      });
    } else {
      // Expression mode - fixed number of outputs
      const count = nodeData.numberOfOutputs || 4;
      for (let i = 0; i < count; i++) {
        dynamicOutputs.push({
          id: `output_${i}`,
          label: `Output ${i}`,
        });
      }
    }

    // Add fallback output if configured
    if (fallbackOutput === 'extra') {
      dynamicOutputs.push({
        id: 'fallback',
        label: 'Fallback',
        isFallback: true,
      });
    }

    // Ensure at least one output
    if (dynamicOutputs.length === 0) {
      dynamicOutputs.push({
        id: 'output_0',
        label: 'Output 0',
      });
    }

    return dynamicOutputs;
  }, [mode, nodeData.rules?.values, nodeData.numberOfOutputs, fallbackOutput]);

  // Check if a handle is connected
  const isAnchorConnected = useMemo(() => {
    return (handleId: string, handleType: 'source' | 'target' = 'source'): boolean => {
      return edges.some(edge => {
        if (handleType === 'source') {
          if (edge.source === id) {
            if (edge.sourceHandle !== null && edge.sourceHandle !== undefined) {
              return edge.sourceHandle === handleId;
            }
            return handleId === 'output_0';
          }
        } else {
          if (edge.target === id) {
            if (edge.targetHandle !== null && edge.targetHandle !== undefined) {
              return edge.targetHandle === handleId;
            }
            return handleId === 'default';
          }
        }
        return false;
      });
    };
  }, [edges, id]);

  // Height grows based on number of outputs (minimum 96px for standard size)
  // Standard width is 96px (w-24 Tailwind class)
  const nodeHeight = Math.max(96, outputs.length * 32);

  // Calculate handle position for each output - distribute evenly within the card
  const getHandlePositionPx = (index: number, total: number) => {
    if (total === 1) {
      return nodeHeight / 2; // Center vertically
    }
    // Distribute handles evenly with 25% margins
    const marginPercent = 0.25;
    const usableHeight = nodeHeight * (1 - 2 * marginPercent);
    const startOffset = nodeHeight * marginPercent;
    const spacing = usableHeight / (total - 1);
    return startOffset + (spacing * index);
  };

  return (
    <NodeContextMenu nodeId={id}>
      <div
        ref={nodeRef}
        className="relative group flex flex-col items-center isolate"
      >
        {/* Input Handle - centered vertically on left side */}
        <Handle
          type="target"
          position={Position.Left}
          id="default"
          isConnectable={!isDisabled}
          className="w-5 h-5 border-2 border-white transition-all hover:scale-125"
          style={{
            background: workflowColors.handle.default,
            top: `${nodeHeight / 2}px`,
            transform: 'translateY(-50%)',
            left: '-10px',
          }}
        />

        {/* Node Card - matches GenericNode styling */}
        <div
          className={`w-24 flex items-center justify-center rounded-xl border-[3px] hover:shadow-lg transition-all duration-300 relative z-10 ${
            isDisabled ? 'opacity-60' : ''
          } cursor-pointer ${
            isExecuting ? 'animate-pulse ring-2 ring-accent-info-500 scale-110' : ''
          } ${
            executionStatus === 'success' ? 'ring-2 ring-accent-success-500' : ''
          } ${
            executionStatus === 'failed' ? 'ring-2 ring-destructive' : ''
          }`}
          style={{
            height: `${nodeHeight}px`,
            background: isDisabled ? 'var(--muted)' : displayDef.bgColor,
            borderColor: isDisabled ? 'var(--border)' : displayDef.color,
            boxShadow: '0 1px 3px rgba(0, 0, 0, 0.1)',
          }}
        >
          {/* Icon centered in card - color matches border */}
          <NodeIcon
            icon={displayDef.icon}
            iconType={displayDef.iconType}
            iconColor={displayDef.color}
            color={displayDef.color}
            className={`h-8 w-8 ${isExecuting ? 'animate-spin' : ''}`}
          />

          {/* Execution status indicator */}
          {!isDisabled && isExecuting && (
            <div className="absolute -top-1 -right-1 w-3 h-3 bg-accent-info-500 rounded-full animate-ping" />
          )}
          {!isDisabled && executionStatus === 'success' && (
            <div className="absolute -top-1 -right-1 w-3 h-3 bg-accent-success-500 rounded-full" />
          )}
          {!isDisabled && executionStatus === 'failed' && (
            <div className="absolute -top-1 -right-1 w-3 h-3 bg-destructive rounded-full" />
          )}
        </div>

        {/* Label underneath the node - matches GenericNode */}
        <div className="mt-2 w-24">
          <div className="text-xs font-medium text-foreground text-center line-clamp-2">
            {data?.label || displayDef.label}
          </div>
        </div>

        {/* Output Handles with labels - matches GenericNode pattern */}
        {outputs.map((output, index) => {
          const topPositionPx = getHandlePositionPx(index, outputs.length);
          const isConnected = isAnchorConnected(output.id);
          const showLabel = outputs.length > 1; // Only show labels when multiple outputs

          return (
            <div
              key={output.id}
              className="absolute right-0 group/anchor"
              style={{
                top: `${topPositionPx}px`,
              }}
            >
              {!isConnected ? (
                <>
                  <Handle
                    id={output.id}
                    type="source"
                    position={Position.Right}
                    isConnectable={!isDisabled}
                    className="w-5 h-5 border-2 border-white transition-all hover:scale-125 cursor-crosshair"
                    style={{
                      background: output.isFallback
                        ? '#f59e0b' // Amber for fallback
                        : workflowColors.handle.default,
                      top: 0,
                      transform: 'translateY(-50%)',
                      right: '-10px',
                    }}
                  />
                  {/* Plus button - matches GenericNode */}
                  <button
                    className="absolute left-3 top-0 -translate-y-1/2 w-7 h-7 flex items-center justify-center rounded-full bg-primary hover:bg-primary/90 text-primary-foreground opacity-0 group-hover/anchor:opacity-100 shadow-lg hover:shadow-xl transition-all hover:scale-110 pointer-events-auto cursor-pointer z-20"
                    onClick={(e) => {
                      e.stopPropagation();
                      if (nodeRef.current) {
                        const nodeRect = nodeRef.current.getBoundingClientRect();
                        const handleY = nodeRect.top + topPositionPx;
                        const position = {
                          x: nodeRect.right + 30,
                          y: handleY,
                        };
                        onAddNodeClick?.(id, output.id, undefined, position);
                      }
                    }}
                    title="Add node"
                    disabled={isDisabled}
                  >
                    <Plus className="w-4 h-4" strokeWidth={2.5} />
                  </button>

                  {/* Output label - shown to the right of handle */}
                  {showLabel && (
                    <div
                      className={`absolute text-[10px] font-medium whitespace-nowrap pointer-events-none select-none bg-background/80 px-1 rounded ${
                        output.isFallback ? 'text-amber-600' : 'text-muted-foreground'
                      }`}
                      style={{
                        left: 16,
                        top: '50%',
                        transform: 'translateY(-50%)',
                      }}
                    >
                      {output.label}
                    </div>
                  )}
                </>
              ) : (
                <>
                  <Handle
                    id={output.id}
                    type="source"
                    position={Position.Right}
                    isConnectable={false}
                    className="w-5 h-5 border-2 border-white"
                    style={{
                      background: output.isFallback
                        ? '#f59e0b' // Amber for fallback
                        : workflowColors.handle.default,
                      top: 0,
                      transform: 'translateY(-50%)',
                      right: '-10px',
                    }}
                  />

                  {/* Output label - shown to the right of handle */}
                  {showLabel && (
                    <div
                      className={`absolute text-[10px] font-medium whitespace-nowrap pointer-events-none select-none bg-background/80 px-1 rounded ${
                        output.isFallback ? 'text-amber-600' : 'text-muted-foreground'
                      }`}
                      style={{
                        left: 16,
                        top: '50%',
                        transform: 'translateY(-50%)',
                      }}
                    >
                      {output.label}
                    </div>
                  )}
                </>
              )}
            </div>
          );
        })}
      </div>
    </NodeContextMenu>
  );
}
